
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Link } from 'react-router-dom';

interface Review {
  id: string;
  title: string;
  content: string;
  author: string;
  category: string;
  rating: number;
  cover_image: string;
  genre: string;
  verdict: string;
  bestseller: boolean;
  our_pick: boolean;
  created_at: string;
}

interface ReviewsListProps {
  selectedCategory: string;
  selectedGenre: string;
  selectedRating: number;
  showBestsellers: boolean;
}

export default function ReviewsList({ selectedCategory, selectedGenre, selectedRating, showBestsellers }: ReviewsListProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filteredReviews, setFilteredReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [reviews, selectedCategory, selectedGenre, selectedRating, showBestsellers]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
      } else {
        setReviews(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...reviews];

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(review => review.category === selectedCategory);
    }

    if (selectedGenre !== 'All') {
      filtered = filtered.filter(review => review.genre === selectedGenre);
    }

    if (selectedRating > 0) {
      filtered = filtered.filter(review => review.rating >= selectedRating);
    }

    if (showBestsellers) {
      filtered = filtered.filter(review => review.bestseller);
    }

    setFilteredReviews(filtered);
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    return (
      <div className="flex items-center gap-1">
        {[...Array(fullStars)].map((_, i) => (
          <i key={i} className="ri-star-fill text-yellow-400"></i>
        ))}
        {hasHalfStar && <i className="ri-star-half-fill text-yellow-400"></i>}
        {[...Array(5 - Math.ceil(rating))].map((_, i) => (
          <i key={i} className="ri-star-line text-gray-300"></i>
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Libre Baskerville", serif' }}>
            All Reviews
          </h2>
          <div className="h-6 w-24 bg-gray-300 rounded animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden animate-pulse">
              <div className="md:flex">
                <div className="md:w-48 md:flex-shrink-0">
                  <div className="h-64 md:h-full bg-gray-300"></div>
                </div>
                <div className="p-6 flex-1">
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-6 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded mb-4"></div>
                  <div className="h-20 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Libre Baskerville", serif' }}>
          All Reviews
        </h2>
        <p className="text-gray-600">
          {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {filteredReviews.length === 0 ? (
        <div className="text-center py-12">
          <i className="fas fa-book-open text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No reviews found</h3>
          <p className="text-gray-600">Try adjusting your filters or check back later for new reviews.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="flex">
                <div className="w-32 flex-shrink-0">
                  <img 
                    src={review.cover_image} 
                    alt={review.title}
                    className="w-full h-48 object-cover object-top"
                  />
                </div>
                <div className="flex-1 p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{review.title}</h3>
                      <p className="text-gray-600">by {review.author}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    {review.our_pick && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <span>🍁</span>
                        Our Pick
                      </span>
                    )}
                    {review.bestseller && (
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <i className="ri-award-line"></i>
                        Bestseller
                      </span>
                    )}
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                      {review.genre}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm mb-4 line-clamp-2">{review.content.substring(0, 200)}...</p>
                  <div className="flex items-center justify-between">
                    <div></div>
                    <Link 
                      to={`/reviews/${review.id}`}
                      className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors font-medium text-sm"
                    >
                      Read Full Review
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
